import React, { useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { DeleteBudgetDialog } from './DeleteBudgetDialog';
import { useDeleteBudget } from '../hooks/useBudgetQueries';
import { getBudget } from '../db';
import { Budget } from '../types/budget';
import { useTranslation } from 'react-i18next';
import { useToast } from '../contexts/ToastContext';

interface DeleteBudgetButtonProps {
  budgetId: number;
  showText?: boolean;
  className?: string;
  onSuccess?: () => void;
}

export const DeleteBudgetButton: React.FC<DeleteBudgetButtonProps> = ({
  budgetId,
  showText = false,
  className = '',
  onSuccess,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [budget, setBudget] = useState<Budget | null>(null);
  const deleteBudgetMutation = useDeleteBudget();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const handleOpenDialog = () => {
    const budgetData = getBudget(budgetId);
    setBudget(budgetData);
    setDeleteDialogOpen(true);
  };

  const handleDeleteBudget = () => {
    deleteBudgetMutation.mutate(budgetId, {
      onSuccess: () => {
        setDeleteDialogOpen(false);

        // Show success toast notification
        showToast(t('budget.deleteSuccess', { name: budget?.name || '' }), 'success');

        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess();
        }
      },
      onError: (error) => {
        console.error('Error deleting budget:', error);
        showToast(t('budget.deleteError'), 'error');
      },
    });
  };

  const baseClasses =
    'flex items-center rounded-md rounded-md border border-slate-500 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-slate-500 dark:bg-surface-800 dark:text-slate-300 dark:hover:bg-surface-700';
  const buttonClasses = className ? `${baseClasses} ${className}` : baseClasses;

  return (
    <>
      <button onClick={handleOpenDialog} className={buttonClasses} title={t('budget.delete')}>
        <TrashIcon className={`h-5 w-5 ${showText ? 'mr-2' : ''}`} />
        {showText && <span>{t('budget.delete')}</span>}
      </button>

      <DeleteBudgetDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteBudget}
        budget={budget || undefined}
      />
    </>
  );
};
